// ЧАРТ ШАГА 4 — корпус слоёв, геометрия, линии в кадре покоя. B6-3 `3e619875cbb881f5a852c4ff770513ee`.
//
// ИСТОЧНИК — борд `Life Map - s4 Layered Map v10.html` (sha256 `ed131e8b…e9a5`) ВМЕСТЕ со спекой s4
// `3b419875cbb881bb9cf8e4cd3a7e9da2` (`s743.d1`: борд устарел по четырём пунктам, носитель поведения —
// спека) и таблицами `INTENTIONAL` / `BOARD_LAGS` парити `wizard-s4-board.test.js`. Адреса ниже —
// строки байта v10.
//
// ЧИСТЫЕ ФУНКЦИИ: на входе ползунки конверта, на выходе числа и строки `d`. Разметка и css — экран
// `StepFour.jsx` и `wizard-s4.css`.
//
// ⛔ ЧЕГО ЗДЕСЬ НЕТ, И ЭТО НЕ ПРОПУСК:
//   · силуэта и волн ревила (`silhouette()` `:514`, `reveal()` `:515`) — это B6-4; здесь кадр покоя,
//     то есть `t = 1` у всех семи слоёв, и `meanAt` из `layerPts` `:360` схлопывается в само значение;
//   · мягкого пути источников `.brg` `:396` — снесён целиком (`s457.d1`, мастер п.8);
//   · счётчика дыр `gaps()` `:335` — его печатал только рельс, и снесён он без замены (`s586.d1`).

import { LAYERS as PASSPORT_LAYERS } from '../../render/layersConfig.js';
import { LAYER_COPY } from './deckCopy.js';

// ---- корпус семи слоёв ------------------------------------------------------------------------

/**
 * Цвета, которые экран берёт у БОРДА, а не у паспорта (`s946.d2`, список `INTENTIONAL` парити):
 * бумажная семья разведена по светлоте под 390px (заметка CD `:231`). Живёт только в визарде.
 */
const WIZARD_COLOR = { Authorship: '#A79F90', Lasting: '#8A8375' };

/** Прибавка толщины выбранному слою — своё правило экрана (`v10:555`), не вес слоя. */
export const SELECTED_EXTRA_W = 0.8;

/**
 * Семь слоёв шага 4 в порядке контракта.
 *
 * Веса — ПАСПОРТНЫЕ 1.7 / 1.4, а не 1.9 / 1.5 борда (`s946.d2`, отставание борда в реестре
 * `WIZARD-CANON.md`). Имя и полюса — деки (`LAYER_COPY`): L2 здесь `sense of security` (`s914.d4`),
 * правый полюс wanting — `pulled to many things`, а не `pulled to many` борда. Штрих `dash` —
 * identity-dash паспорта (`s585.d1`: у трёх слоёв, оптом не стрипать).
 */
// ⚠️ Дека и паспорт сводятся ПО КЛЮЧУ, а не по индексу: порядок у обоих — контракт, но совпадение
// порядка здесь не предполагается, а проверяется тестом (`s4Chart.test.js`).
const DECK = Object.fromEntries(LAYER_COPY.map((D) => [D.key, D]));

export const S4_LAYERS = Object.freeze(PASSPORT_LAYERS.map((P) => {
  const D = DECK[P.key];
  return Object.freeze({
    key: P.key,
    name: D.name,
    color: WIZARD_COLOR[P.key] ?? P.color,
    w: P.w,
    dash: P.dash,
    poles: D.poles,
  });
}));

// ---- геометрия (`v10:312`, `:336–339`) ----------------------------------------------------------

/** Ширина кадра и отступы по краям — `G` борда `:312`. */
export const G = Object.freeze({ W: 350, L: 8, R: 342, T: 30 });
/** Высота чарта — `CH` борда `:315` (322, освобождённые 72px отданы чарту в v8). */
export const CH = 322;
const B = CH - 34; // низ полосы значений, `geomY()` `:336`

/** Протяжка — с девяти этапов и только по тумблеру (`pannable()` `:316`). */
export const PAN_FROM = 9;

/**
 * Геометрия колонок на N этапов. Колонки РАВНЫЕ, не по годам (дев-нота борда `:267`).
 *
 * @param {number} n число этапов
 * @param {{pan?: boolean}} [opts] включена ли протяжка (`close up ⤢`)
 */
export function s4Geometry(n, { pan = false } = {}) {
  const pannable = pan && n >= PAN_FROM;
  // `chartHTML()` `:384`: в протяжке кадр растягивается на n/6 ширины.
  const W = pannable ? Math.round(G.W * (n / 6)) : G.W;
  const span = W - G.L - (G.W - G.R);
  return {
    n,
    W,
    pannable,
    top: G.T,
    bot: B,
    xMid: (i) => G.L + ((i + 0.5) / n) * span,
    xEdge: (i) => G.L + (i / n) * span,
  };
}

/** Шкала −3…+3 на полосу `T…B`, ноль посередине (`yV` `:337`). */
export function yV(v) {
  return (G.T + B) / 2 - (v / 3) * ((B - G.T) / 2);
}

// ---- серии слоёв ------------------------------------------------------------------------------

/**
 * Серия слоя по этапам, СТАРЫЙ СЛЕВА — порядок ползунков конверта (`session.input.sliders`, дека
 * хронологична). `null` = слой на этапе не тронут (`val()` `:319`).
 */
export function layerSeries(sliders, li) {
  return sliders.map((row) => {
    const v = row ? row[li] : null;
    return Number.isFinite(v) ? v : null;
  });
}

/** Слой тронут хоть на одном этапе (`live()` `:334`). Нетронутый целиком — линии нет (`s601.d1`). */
export const isLive = (series) => series.some((v) => v !== null);

/**
 * Форма для рисования: пропуск интерполируется между ближайшими ответами, по краям держится ровно
 * (`layerPts()` `:352–359`). Только форма — в чтение и доказательства не идёт.
 */
export function filledSeries(series) {
  const n = series.length;
  return series.map((v, i) => {
    if (v !== null) return v;
    let p = null;
    let q = null;
    for (let k = i - 1; k >= 0; k--) if (series[k] !== null) { p = { i: k, v: series[k] }; break; }
    for (let k = i + 1; k < n; k++) if (series[k] !== null) { q = { i: k, v: series[k] }; break; }
    if (p && q) return p.v + (q.v - p.v) * ((i - p.i) / (q.i - p.i));
    return p ? p.v : q.v;
  });
}

const f1 = (x) => x.toFixed(1);

/** Catmull-Rom t = 0.5, один спан на пару соседних колонок (`layerGeom()` `:364–365`). */
function spanD(p, i, K = 0.5) {
  const p0 = p[i - 1] || p[i], p1 = p[i], p2 = p[i + 1], p3 = p[i + 2] || p2;
  return `M ${f1(p1.x)},${f1(p1.y)} C ${f1(p1.x + (p2.x - p0.x) * (K / 3))},${f1(p1.y + (p2.y - p0.y) * (K / 3))} ${f1(p2.x - (p3.x - p1.x) * (K / 3))},${f1(p2.y - (p3.y - p1.y) * (K / 3))} ${f1(p2.x)},${f1(p2.y)}`;
}

/** Сглаженный путь по точкам одним `M … C …` (`smooth()` `:342–346`) — для полосы compare. */
function smooth(pts, t = 0.5) {
  if (pts.length < 2) return '';
  let d = `M ${f1(pts[0].x)},${f1(pts[0].y)}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] || pts[i], p1 = pts[i], p2 = pts[i + 1], p3 = pts[i + 2] || p2;
    d += ` C ${f1(p1.x + (p2.x - p0.x) * (t / 3))},${f1(p1.y + (p2.y - p0.y) * (t / 3))} ${f1(p2.x - (p3.x - p1.x) * (t / 3))},${f1(p2.y - (p3.y - p1.y) * (t / 3))} ${f1(p2.x)},${f1(p2.y)}`;
  }
  return d;
}

/**
 * Линия слоя в кадре покоя: ОДИН сплошной путь полным весом (`s457.d1`) плюс кольца на ответах без
 * отвеченных соседей (`:369–371`, кольцо одиночного ответа вернули по ответу CC).
 *
 * @returns {{d: string, rings: Array<{x: number, y: number}>}|null} `null` — слой не тронут вовсе
 */
export function layerGeom(series, geom, unfold = null) {
  if (!isLive(series)) return null;
  // Reveal (B6-4, `layerPts()` `:360`): точка едет от среднего этапа к своему значению по ходу
  // волны `t` — `m + (v − m)·t`. Без `unfold` — кадр покоя, ровно как до reveal.
  const at = unfold === null
    ? (v) => v
    : (v, i) => unfold.means[i] + (v - unfold.means[i]) * unfold.t;
  const pts = filledSeries(series).map((v, i) => ({ x: geom.xMid(i), y: yV(at(v, i)), real: series[i] !== null }));
  const spans = [];
  for (let i = 0; i < pts.length - 1; i++) spans.push(spanD(pts, i));
  const rings = pts
    .filter((pt, i) => pt.real && !(pts[i - 1] && pts[i - 1].real) && !(pts[i + 1] && pts[i + 1].real))
    .map(({ x, y }) => ({ x, y }));
  return { d: spans.join(' '), rings };
}

/**
 * Среднее по ТРОНУТЫМ слоям на каждом этапе — исходная форма reveal (`meanAt()` `:347`).
 *
 * ⚠️ ПУСТОЙ ЭТАП — НЕ НОЛЬ. Борд при пустом наборе возвращает `0` и роняет точку на нулевую ось;
 * спека права (`s836.d1`/`s836.d3`): пустой этап = `null`, форма тянется интерполяцией соседей —
 * тем же `filledSeries`, что ведёт линию слоя через пропуск. Входа в состояние при гейте шага 3
 * нет (`s495.d1`), это защита от бага, не ветка продукта. Ни одного ответа вовсе — `null`.
 */
export function stageMeans(seriesList) {
  const n = seriesList[0]?.length ?? 0;
  const raw = Array.from({ length: n }, (_, i) => {
    const v = seriesList.map((s) => s[i]).filter((x) => x !== null);
    return v.length ? v.reduce((a, x) => a + x, 0) / v.length : null;
  });
  return raw.some((v) => v !== null) ? filledSeries(raw) : null;
}

/** Силуэт средних (`silhouette()` `:514`) одним сглаженным путём; нет средних — пусто. */
export function silhouettePath(means, geom) {
  if (means === null) return '';
  return smooth(means.map((m, i) => ({ x: geom.xMid(i), y: yV(m) })));
}

/**
 * Полоса разрыва между выбранными слоями (compare, `bandPath()` `:373–380`). Строится только по
 * настоящим ответам и рвётся там, где у выбранных меньше двух ответов.
 */
export function bandPath(seriesList, geom) {
  const live = seriesList.filter(isLive);
  if (live.length < 2) return '';
  const runs = [];
  let cur = [];
  for (let i = 0; i < geom.n; i++) {
    const v = live.map((s) => s[i]).filter((x) => x !== null);
    if (v.length < 2) {
      if (cur.length > 1) runs.push(cur);
      cur = [];
      continue;
    }
    cur.push({ x: geom.xMid(i), hi: yV(Math.max(...v)), lo: yV(Math.min(...v)) });
  }
  if (cur.length > 1) runs.push(cur);
  return runs.map((r) => {
    const top = smooth(r.map((p) => ({ x: p.x, y: p.hi })));
    const bot = r.slice().reverse().map((p) => ({ x: p.x, y: p.lo }));
    return `${top} ${smooth(bot).replace(/^M/, 'L')} Z`;
  }).join(' ');
}

/**
 * Год-тики под чартом (`ticks()` `:381–383`): шаг 1 при N ≤ 5, 2 при N ≤ 9, иначе 3; подпись —
 * две последние цифры года с апострофом; последний тик — слово `now`.
 *
 * @param {Array<number|null>} years год каждого этапа (конец этапа), старый слева
 */
export function yearTicks(years, geom) {
  const n = geom.n;
  const step = n <= 5 ? 1 : (n <= 9 ? 2 : 3);
  const out = [];
  for (let i = 0; i < n - 1; i += step) {
    // Год, которого нет, не печатается пустым апострофом: тика нет, соседи на месте.
    if (Number.isFinite(years[i])) out.push({ x: geom.xMid(i), label: `'${String(Math.floor(years[i])).slice(-2)}`, now: false });
  }
  out.push({ x: geom.xMid(n - 1), label: 'now', now: true });
  return out;
}

/** Режим вида — следствие числа выбранных, не переключатель (`mode()` `:341`). */
export function viewMode(selected) {
  if (selected.length === 0) return 'constellation';
  return selected.length === 1 ? 'solo' : 'compare';
}
